import { lazy, type ComponentType } from "react";

// Every route is a hashed chunk loaded on demand. Two things make that import
// fail in the wild, and neither is a bug in the page itself:
//
//  1. a transient network failure (flaky mobile connection, a crawler with a
//     stingy fetch budget, a CDN hiccup) — retrying works;
//  2. the tab is running an index.html from a *previous* deploy, so the chunk
//     hash it asks for no longer exists on the CDN. This is what bots hit most,
//     since they cache HTML for days and execute it much later. Retrying can't
//     help — only a fresh index.html has the current hashes, so we reload once.
//
// A third case wears the same error message but belongs to neither: a
// *third-party* module that failed to load (a library pulled from a public CDN,
// say). Reloading can't fix that one, and filing it as a stale build hides it,
// so isStaleChunkError checks the failing URL is one of our own build assets
// before claiming it.
//
// Without this, either of the first two cases took down the whole tree: React re-threw out of
// Suspense into the top-level ErrorBoundary and the visitor got an error screen
// (and we got a "render crash" in the Bugs tab) for what is really just a file
// that moved.

const STALE_CHUNK_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Loading chunk .* failed|Importing a module script failed|Unable to preload CSS/i;

/** Vite puts the failing module's absolute URL in the error message. */
const extractModuleUrl = (error: unknown): string | null => {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return message.match(/https?:\/\/\S+?\.[cm]?js\b/)?.[0] ?? null;
};

/** Vite serves every hashed chunk of a build from /assets/. We match on that
 *  path rather than comparing origins on purpose: one build serves both
 *  ernest-app.com and kithe.club, so an origin check would misfile a kithe
 *  chunk whenever the Bugs tab is read from ernest, and the other way round. */
const isOwnBuildAsset = (url: string | null): boolean => {
  // "Loading chunk 42 failed" and friends carry no URL at all. Those only ever
  // come from our own loader, so the message on its own is enough to go on.
  if (!url) return true;
  try {
    return new URL(url).pathname.startsWith("/assets/");
  } catch {
    return true;
  }
};

export const isStaleChunkError = (error: unknown): boolean => {
  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : "";
  if (!STALE_CHUNK_PATTERN.test(message)) return false;
  // A third-party module that fails to load produces the exact same message as
  // a chunk that moved, and it is not a stale build: reloading can't fix it,
  // and filing it as one greys it out in the Bugs tab. That is how a pdf.js
  // worker fetched from unpkg sat there looking benign. See PdfViewer.tsx.
  return isOwnBuildAsset(extractModuleUrl(error));
};

const CHUNK_RELOAD_KEY = "kithe_chunk_reload_attempted";

/**
 * Reload the page to pick up the current build — at most once per session, so
 * a chunk that is missing for some *other* reason can't put us in a reload
 * loop. Returns whether a reload was actually started.
 */
export const reloadOnceForStaleChunk = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return false;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
  } catch {
    // storage unavailable — we can't guard against a loop, so don't reload
    return false;
  }
  window.location.reload();
  return true;
};

/** Called on a clean mount: this session's chunks are current, so a future
 *  stale-chunk error is worth one more auto-reload. */
export const clearChunkReloadFlag = () => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // storage unavailable — the reload guard just won't persist
  }
};

const RETRY_DELAY_MS = 500;

/**
 * Drop-in replacement for `React.lazy` that survives a failed chunk fetch.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (!isStaleChunkError(error)) throw error;

      // Re-fetch once, cache-busted so we don't just get the browser's cached
      // failure back. Enough for a blip, or for a response that was cached
      // mid-deploy while the file was briefly unavailable.
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      const url = extractModuleUrl(error);
      try {
        return url
          ? ((await import(/* @vite-ignore */ `${url}?retry=${Date.now()}`)) as {
              default: T;
            })
          : await factory();
      } catch {
        // still gone — the hash itself is stale, not the response
      }

      if (reloadOnceForStaleChunk()) {
        // The reload is in flight; never resolving keeps the Suspense fallback
        // on screen instead of flashing an error screen on the way out.
        await new Promise<never>(() => {});
      }
      throw error;
    }
  });
}
